import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import {
  ArrowDownward,
  ArrowUpward,
  Autorenew,
  Block,
  CheckCircleOutline,
  Close,
  ExpandLess,
  ExpandMore,
  RotateLeft,
  Smartphone,
  Warning
} from "@material-ui/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis
} from "recharts";
import moment from "moment-timezone";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import { formatCompanyDateTime, resolveCompanyTimezone } from "../../utils/companyTimezone";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === "dark";

  return {
    pageRoot: {
      background: isDark
        ? "linear-gradient(180deg, #0b1120 0%, #111827 100%)"
        : "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
      minHeight: "100%",
      overflowX: "hidden"
    },
    dashboardPageWrapper: {
      padding: 10,
      height: "100%",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minWidth: 0
    },
    mainPaper: {
      flex: 1,
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
      padding: theme.spacing(3),
      borderRadius: 10,
      backgroundColor: "transparent !important",
      boxShadow: "none",
      ...theme.scrollbarStyles
    },
    hero: {
      borderRadius: 24,
      padding: theme.spacing(2.5, 3),
      marginBottom: theme.spacing(2.5),
      color: "#fff",
      background: isDark
        ? "linear-gradient(135deg, #1d4ed8 0%, #0f172a 55%, #111827 100%)"
        : "linear-gradient(135deg, #2563eb 0%, #4338ca 55%, #7c3aed 100%)",
      boxShadow: "0 20px 50px rgba(37,99,235,0.20)"
    },
    heroTitle: {
      fontWeight: 800,
      letterSpacing: "-0.03em"
    },
    heroSubtitle: {
      opacity: 0.92,
      marginTop: theme.spacing(0.75),
      maxWidth: 900
    },
    heroPill: {
      minWidth: 132,
      borderRadius: 18,
      padding: theme.spacing(1.1, 1.4),
      background: "rgba(255,255,255,0.14)",
      border: "1px solid rgba(255,255,255,0.16)"
    },
    sectionCard: {
      borderRadius: 20,
      border: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)"}`,
      background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
      boxShadow: "none"
    },
    kpiCard: {
      height: "100%",
      borderRadius: 20,
      background: isDark ? "rgba(15,23,42,0.78)" : "#fff",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.1)" : "rgba(15,23,42,0.08)"}`
    },
    kpiValue: {
      fontWeight: 800,
      letterSpacing: "-0.03em"
    },
    kpiHint: {
      color: isDark ? "rgba(226,232,240,0.72)" : "rgba(71,85,105,0.88)"
    },
    analyticsCard: {
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(2.5),
      overflow: "hidden"
    },
    analyticsHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(2.5, 3)
    },
    analyticsBody: {
      padding: theme.spacing(0, 3, 3, 3)
    },
    chartShell: {
      height: 280,
      borderRadius: 18,
      padding: theme.spacing(2),
      background: isDark ? "rgba(2,6,23,0.55)" : "#f8fafc",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)"}`
    },
    smartCard: {
      borderRadius: 18,
      padding: theme.spacing(2),
      background: isDark ? "rgba(2,6,23,0.55)" : "#f8fafc",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)"}`
    },
    smartItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      padding: theme.spacing(1, 0),
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)"}`,
      "&:last-child": {
        borderBottom: "none",
        paddingBottom: 0
      }
    },
    cardsList: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2.5),
      width: "100%",
      minWidth: 0
    },
    connectionCard: {
      width: "100%",
      minWidth: 0,
      borderRadius: 24,
      border: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)"}`,
      background: isDark ? "rgba(15,23,42,0.86)" : "#fff",
      boxShadow: isDark
        ? "0 14px 30px rgba(0,0,0,0.20)"
        : "0 18px 40px rgba(15,23,42,0.06)"
    },
    cardContent: {
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      padding: theme.spacing(3),
      "&:last-child": {
        paddingBottom: theme.spacing(3)
      }
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: theme.spacing(2),
      flexWrap: "wrap"
    },
    identityWrap: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5)
    },
    avatar: {
      width: 54,
      height: 54,
      fontWeight: 800,
      background: "linear-gradient(135deg, #2563eb, #7c3aed)"
    },
    connectionName: {
      fontWeight: 800,
      color: isDark ? "#f8fafc" : "#0f172a"
    },
    metaText: {
      color: isDark ? "rgba(226,232,240,0.72)" : "rgba(71,85,105,0.86)"
    },
    chipRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginTop: theme.spacing(1)
    },
    stageChip: {
      fontWeight: 700
    },
    headerMetrics: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      flexWrap: "wrap"
    },
    scoreCard: {
      minWidth: 120,
      maxWidth: "100%",
      borderRadius: 18,
      padding: theme.spacing(1.25, 1.5),
      background: isDark ? "rgba(2,6,23,0.55)" : "#f8fafc",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)"}`
    },
    sectionTitle: {
      fontWeight: 800,
      color: isDark ? "#f8fafc" : "#0f172a"
    },
    tile: {
      height: "100%",
      borderRadius: 18,
      padding: theme.spacing(1.75),
      background: isDark ? "rgba(2,6,23,0.55)" : "#f8fafc",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)"}`
    },
    tileLabel: {
      color: isDark ? "rgba(226,232,240,0.70)" : "rgba(71,85,105,0.86)"
    },
    tileValue: {
      fontWeight: 800,
      color: isDark ? "#f8fafc" : "#0f172a"
    },
    progressTrack: {
      height: 10,
      borderRadius: 999,
      background: "rgba(148,163,184,0.16)"
    },
    sectionBlock: {
      marginTop: theme.spacing(2.25)
    },
    periodTabs: {
      minHeight: 38
    },
    periodTab: {
      minHeight: 38,
      minWidth: 72,
      fontWeight: 700
    },
    recommendationItem: {
      borderRadius: 14,
      padding: theme.spacing(1.25, 1.5),
      background: isDark ? "rgba(30,41,59,0.9)" : "#f8fafc",
      borderLeft: "4px solid #2563eb"
    },
    historyItem: {
      borderRadius: 14,
      padding: theme.spacing(1.25, 1.5),
      background: isDark ? "rgba(2,6,23,0.55)" : "#f8fafc",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)"}`
    },
    campaignChip: {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    actionBar: {
      display: "flex",
      gap: theme.spacing(1),
      flexWrap: "wrap",
      justifyContent: "flex-end",
      width: "100%",
      minWidth: 0
    },
    blockAlert: {
      borderRadius: 18,
      padding: theme.spacing(1.5, 1.75),
      marginTop: theme.spacing(2),
      background: isDark ? "rgba(127,29,29,0.28)" : "#fff5f5",
      border: `1px solid ${isDark ? "rgba(248,113,113,0.28)" : "#fecaca"}`
    },
    emptyState: {
      padding: theme.spacing(6, 2),
      textAlign: "center",
      color: isDark ? "rgba(226,232,240,0.70)" : theme.palette.text.secondary
    },
    fullWidthGridItem: {
      minWidth: 0
    },
    eventTooltip: {
      maxWidth: 320,
      width: 320,
      padding: 0,
      borderRadius: 10,
      background: isDark ? "#0f172a" : "#ffffff",
      color: isDark ? "#e2e8f0" : "#0f172a",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.22)" : "rgba(15,23,42,0.12)"}`,
      boxShadow: isDark
        ? "0 18px 48px rgba(0,0,0,0.42)"
        : "0 18px 48px rgba(15,23,42,0.16)"
    },
    eventTooltipContent: {
      maxHeight: 300,
      overflowY: "auto",
      padding: theme.spacing(1.25),
      ...theme.scrollbarStyles
    },
    eventItem: {
      padding: theme.spacing(0.75, 0)
    },
    eventContactName: {
      fontWeight: 800,
      color: isDark ? "#f8fafc" : "#0f172a"
    },
    eventMetaLine: {
      display: "block",
      color: isDark ? "rgba(226,232,240,0.78)" : "rgba(71,85,105,0.92)",
      lineHeight: 1.45
    },
    eventLabel: {
      fontWeight: 800,
      color: isDark ? "#cbd5e1" : "#334155"
    },
    eventError: {
      color: "#ef4444",
      fontWeight: 800
    },
    eventBody: {
      color: isDark ? "#fbbf24" : "#92400e",
      fontWeight: 700,
      wordBreak: "break-word"
    },
    eventClickableTile: {
      height: "100%",
      cursor: "pointer",
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 3,
        borderRadius: 18
      }
    },
    eventDialogTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingRight: theme.spacing(1)
    },
    eventDialogContent: {
      maxHeight: "70vh",
      overflowY: "auto",
      ...theme.scrollbarStyles
    },
    eventDialogItem: {
      padding: theme.spacing(1.5, 0)
    },
    eventDialogMessage: {
      marginTop: theme.spacing(0.75),
      padding: theme.spacing(1),
      borderRadius: 8,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      background: isDark ? "rgba(2,6,23,0.55)" : "#f8fafc",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.1)" : "rgba(15,23,42,0.08)"}`
    }
  };
});

const STAGE_META = {
  STAGE_0_BLOCKED: { label: i18n.t("campaignsWarmup.stages.blocked"), color: "#ef4444", bg: "rgba(239,68,68,0.14)" },
  STAGE_1_NEW: { label: i18n.t("campaignsWarmup.stages.new"), color: "#64748b", bg: "rgba(100,116,139,0.16)" },
  STAGE_2_INITIAL: { label: i18n.t("campaignsWarmup.stages.initial"), color: "#3b82f6", bg: "rgba(59,130,246,0.14)" },
  STAGE_3_MODERATE: { label: i18n.t("campaignsWarmup.stages.moderate"), color: "#f59e0b", bg: "rgba(245,158,11,0.14)" },
  STAGE_4_CONSERVATIVE: { label: i18n.t("campaignsWarmup.stages.conservative"), color: "#8b5cf6", bg: "rgba(139,92,246,0.14)" },
  STAGE_5_NORMAL: { label: i18n.t("campaignsWarmup.stages.normal"), color: "#10b981", bg: "rgba(16,185,129,0.14)" },
  STAGE_6_HIGH: { label: i18n.t("campaignsWarmup.stages.high"), color: "#06b6d4", bg: "rgba(6,182,212,0.14)" }
};

const DONUT_COLORS = ["#10b981", "#f59e0b", "#ef4444"];

const formatDateTime = (value, timezone) => (
  value ? formatCompanyDateTime(value, timezone) : i18n.t("campaignsWarmup.empty.noData")
);

const formatSeconds = value => {
  if (value === null || value === undefined) return i18n.t("campaignsWarmup.empty.noData");
  const total = Number(value);
  if (Number.isNaN(total)) return i18n.t("campaignsWarmup.empty.noData");
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}m ${seconds}s`;
};

const formatWaitingWarmupNext = (nextRetryAt, timezone) => {
  if (!nextRetryAt) return "Aguardando próxima janela";
  if (moment(nextRetryAt).isSameOrBefore(moment())) return "Na fila para retomada";
  return formatDateTime(nextRetryAt, timezone);
};

const safeNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getStageMeta = stage => STAGE_META[stage] || {
  label: stage || i18n.t("campaignsWarmup.empty.noStage"),
  color: "#64748b",
  bg: "rgba(100,116,139,0.16)"
};

const getHealthColor = score => {
  if (score >= 90) return "#10b981";
  if (score >= 70) return "#3b82f6";
  if (score >= 60) return "#f59e0b";
  return "#ef4444";
};

const getBlockedState = conn => {
  const autoBlocked = !!conn.campaignBlockedUntil && moment(conn.campaignBlockedUntil).isAfter(moment());
  const manualBlocked = !!conn.campaignManualBlocked;
  return { autoBlocked, manualBlocked };
};

const getStatusTone = conn => {
  const { autoBlocked, manualBlocked } = getBlockedState(conn);
  const hasBaseline = hasWarmupBaseline(conn);

  if (manualBlocked || autoBlocked || conn.campaignWarmupStage === "STAGE_0_BLOCKED") {
    return {
      label: manualBlocked ? i18n.t("campaignsWarmup.labels.manualBlock") : i18n.t("campaignsWarmup.status.blocked"),
      kind: manualBlocked ? "manualBlocked" : "blocked",
      color: "#ef4444",
      bg: "rgba(239,68,68,0.14)"
    };
  }

  if (!hasBaseline) {
    return {
      label: i18n.t("campaignsWarmup.status.awaitingBaseline"),
      kind: "awaitingBaseline",
      color: "#64748b",
      bg: "rgba(100,116,139,0.16)"
    };
  }

  if (safeNumber(conn.campaignHealthScore) >= 80 && safeNumber(conn.campaignSpamRiskScore) < 20) {
    return {
      label: i18n.t("campaignsWarmup.status.healthy"),
      kind: "healthy",
      color: "#10b981",
      bg: "rgba(16,185,129,0.14)"
    };
  }

  if (safeNumber(conn.campaignHealthScore) >= 60) {
    return {
      label: i18n.t("campaignsWarmup.status.attention"),
      kind: "attention",
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.14)"
    };
  }

  return {
    label: i18n.t("campaignsWarmup.status.critical"),
    kind: "critical",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.14)"
  };
};

const getConnectionNumber = conn =>
  conn.number || conn.phone || conn.phoneNumber || conn.formattedNumber || i18n.t("campaignsWarmup.empty.noData");

const hasWarmupBaseline = conn => {
  const trackedVolume =
    safeNumber(conn.campaignSentToday) +
    safeNumber(conn.campaignSuccessCountToday) +
    safeNumber(conn.campaignFailureCountToday) +
    safeNumber(conn.campaignOptOutCountToday) +
    safeNumber(conn.campaignInvalidCountToday) +
    safeNumber(conn.campaignDeliveredCountToday) +
    safeNumber(conn.campaignReadCountToday) +
    safeNumber(conn.campaignReplyCountToday);

  return trackedVolume > 0 || !!conn.campaignLastSentAt;
};

const hasPeriodBaseline = period => {
  if (!period) return false;
  const tracked =
    safeNumber(period.sentCount) +
    safeNumber(period.deliveredCount) +
    safeNumber(period.readCount) +
    safeNumber(period.replyCount) +
    safeNumber(period.optOutCount) +
    safeNumber(period.positiveCount) +
    safeNumber(period.negativeCount) +
    safeNumber(period.neutralCount);

  return tracked > 0;
};

const MiniGauge = ({ score, label, unavailable = false }) => {
  const radius = 21;
  const circumference = 2 * Math.PI * radius;
  const safeScoreValue = Math.max(0, Math.min(safeNumber(score), 100));
  const dashoffset = circumference - (safeScoreValue / 100) * circumference;
  const color = unavailable ? "#94a3b8" : getHealthColor(safeScoreValue);

  return (
    <Box display="flex" alignItems="center" style={{ gap: 12 }}>
      <Box position="relative" width={58} height={58}>
        <svg width="58" height="58" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="29" cy="29" r={radius} fill="transparent" stroke="rgba(148,163,184,0.18)" strokeWidth="6" />
          <circle
            cx="29"
            cy="29"
            r={radius}
            fill="transparent"
            stroke={color}
            strokeWidth="6"
            strokeDasharray={circumference}
            strokeDashoffset={dashoffset}
            strokeLinecap="round"
          />
        </svg>
        <Box position="absolute" top={0} left={0} right={0} bottom={0} display="flex" alignItems="center" justifyContent="center">
          <Typography variant="caption" style={{ fontWeight: 800 }}>
            {unavailable ? "—" : safeScoreValue}
          </Typography>
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="textSecondary">{label}</Typography>
        <Typography variant="body2" style={{ fontWeight: 800 }}>
          {unavailable ? i18n.t("campaignsWarmup.empty.noBaseline") : `${safeScoreValue}/100`}
        </Typography>
      </Box>
    </Box>
  );
};

const MetricTile = ({ classes, label, value, caption, tone }) => (
  <Box className={classes.tile}>
    <Typography variant="caption" className={classes.tileLabel}>{label}</Typography>
    <Typography
      variant="h6"
      className={classes.tileValue}
      style={tone ? { color: tone } : undefined}
    >
      {value === null || value === undefined || value === "" ? i18n.t("campaignsWarmup.empty.noData") : value}
    </Typography>
    {caption ? (
      <Typography variant="caption" className={classes.tileLabel}>
        {caption}
      </Typography>
    ) : null}
  </Box>
);

const WaitingWarmupTile = ({ classes, summary, timezone }) => {
  const data = summary || {};
  const count = safeNumber(data.count);
  const hourlyUsed = safeNumber(data.hourlyUsed);
  const hourlyLimit = safeNumber(data.hourlyLimit);
  const dailyUsed = safeNumber(data.dailyUsed);
  const dailyLimit = safeNumber(data.dailyLimit);

  return (
    <Box className={classes.tile}>
      <Typography variant="caption" className={classes.tileLabel}>
        Aguardando warmup
      </Typography>
      <Typography variant="h6" className={classes.tileValue} style={{ color: "#2563eb" }}>
        {count}
      </Typography>
      <Typography variant="caption" className={classes.eventMetaLine}>
        <span className={classes.eventLabel}>Próxima rodada:</span> {formatWaitingWarmupNext(data.nextRetryAt, timezone)}
      </Typography>
      <Typography variant="caption" className={classes.eventMetaLine}>
        <span className={classes.eventLabel}>Motivo:</span> {data.reason || "Aguardando warmup"}
      </Typography>
      <Typography variant="caption" className={classes.eventMetaLine}>
        <span className={classes.eventLabel}>Limite:</span> {hourlyUsed}/{hourlyLimit} hora · {dailyUsed}/{dailyLimit} dia
      </Typography>
      <Typography variant="caption" className={classes.eventMetaLine}>
        <span className={classes.eventLabel}>Retomada automática:</span> {data.autoResume ? "sim" : "não"}
      </Typography>
    </Box>
  );
};

const formatEventStatus = status => {
  if (!status) return i18n.t("campaignsWarmup.empty.noData");
  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
};

const getEventSummaryLabel = type => {
  if (type === "failure") return "Erro";
  if (type === "warmup") return "Motivo";
  return "Status";
};

const getEventSummaryValue = (event, type) => {
  if (type === "failure") return event.errorMessage || formatEventStatus(event.status);
  if (type === "warmup") return event.errorMessage || i18n.t("campaignsWarmup.empty.noData");
  return formatEventStatus(event.status);
};

const EventHoverContent = ({ classes, events, type, timezone }) => {
  const safeEvents = Array.isArray(events) ? events : [];
  const visibleEvents = safeEvents.slice(0, 5);
  const hiddenCount = Math.max(safeEvents.length - visibleEvents.length, 0);

  if (!visibleEvents.length) {
    return (
      <Box className={classes.eventTooltipContent}>
        <Typography variant="body2" className={classes.eventMetaLine}>
          Nenhum evento recente para exibir.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.eventTooltipContent}>
      {visibleEvents.map((event, index) => (
        <React.Fragment key={`${type}-${event.createdAt || index}-${event.contactNumber || index}`}>
          <Box className={classes.eventItem}>
            <Typography variant="body2" className={classes.eventContactName}>
              {event.contactName || i18n.t("campaignsWarmup.empty.noData")}
            </Typography>
            <Typography variant="caption" className={classes.eventMetaLine}>
              {event.contactNumber || i18n.t("campaignsWarmup.empty.noData")}
            </Typography>
            <Typography variant="caption" className={classes.eventMetaLine}>
              {formatDateTime(event.createdAt, timezone)}
            </Typography>
            <Typography variant="caption" className={classes.eventMetaLine}>
              <span className={classes.eventLabel}>{getEventSummaryLabel(type)}:</span>{" "}
              {getEventSummaryValue(event, type)}
            </Typography>
          </Box>
          {index < visibleEvents.length - 1 ? <Divider /> : null}
        </React.Fragment>
      ))}
      {hiddenCount > 0 ? (
        <>
          <Divider />
          <Box pt={1}>
            <Typography variant="caption" className={classes.eventMetaLine}>
              + {hiddenCount} eventos. Clique para ver todos.
            </Typography>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

const EventMetricTooltip = ({ children, classes, events, type, timezone, onClick }) => (
  <Tooltip
    interactive
    placement="top"
    enterDelay={250}
    leaveDelay={120}
    classes={{ tooltip: classes.eventTooltip }}
    title={<EventHoverContent classes={classes} events={events} type={type} timezone={timezone} />}
  >
    <Box
      className={classes.eventClickableTile}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

const eventDialogTitles = {
  success: "Eventos de sucesso",
  failure: "Eventos de falha",
  optOut: "Eventos de opt-out",
  warmup: "Eventos aguardando warmup"
};

const EventDetailsDialog = ({ classes, open, onClose, title, events, type, timezone }) => {
  const safeEvents = Array.isArray(events) ? events : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.eventDialogTitle}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {title || eventDialogTitles[type] || "Eventos"}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Fechar">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.eventDialogContent}>
        {!safeEvents.length ? (
          <Typography variant="body2" color="textSecondary">
            Nenhum evento recente para exibir.
          </Typography>
        ) : safeEvents.map((event, index) => (
          <React.Fragment key={`${type}-dialog-${event.createdAt || index}-${event.contactNumber || index}`}>
            <Box className={classes.eventDialogItem}>
              <Typography variant="subtitle2" className={classes.eventContactName}>
                {event.contactName || i18n.t("campaignsWarmup.empty.noData")}
              </Typography>
              <Typography variant="body2" className={classes.eventMetaLine}>
                <span className={classes.eventLabel}>Número:</span> {event.contactNumber || i18n.t("campaignsWarmup.empty.noData")}
              </Typography>
              <Typography variant="body2" className={classes.eventMetaLine}>
                <span className={classes.eventLabel}>Campanha:</span> {event.campaignName || i18n.t("campaignsWarmup.empty.noData")}
              </Typography>
              <Typography variant="body2" className={classes.eventMetaLine}>
                <span className={classes.eventLabel}>Conexão:</span> {event.whatsappName || i18n.t("campaignsWarmup.empty.noData")}
              </Typography>
              <Typography variant="body2" className={classes.eventMetaLine}>
                <span className={classes.eventLabel}>Data/hora:</span> {formatDateTime(event.createdAt, timezone)}
              </Typography>
              <Typography variant="body2" className={classes.eventMetaLine}>
                <span className={classes.eventLabel}>Status:</span> {formatEventStatus(event.status)}
              </Typography>
              {event.errorMessage ? (
                <Typography variant="body2" className={classes.eventMetaLine}>
                  <span className={classes.eventLabel}>Erro:</span>{" "}
                  <span className={classes.eventError}>{event.errorMessage}</span>
                </Typography>
              ) : null}
              {type === "warmup" ? (
                <Typography variant="body2" className={classes.eventMetaLine}>
                  <span className={classes.eventLabel}>Próxima tentativa:</span> {formatWaitingWarmupNext(event.nextRetryAt, timezone)}
                </Typography>
              ) : null}
              <Typography variant="body2" className={classes.eventMetaLine}>
                <span className={classes.eventLabel}>ID da campanha:</span> {event.campaignId || i18n.t("campaignsWarmup.empty.noData")}
              </Typography>
              <Typography variant="body2" className={classes.eventMetaLine}>
                <span className={classes.eventLabel}>ID do envio:</span> {event.campaignShippingId || i18n.t("campaignsWarmup.empty.noData")}
              </Typography>
              <Box className={classes.eventDialogMessage}>
                <Typography variant="caption" className={classes.eventLabel}>
                  Mensagem
                </Typography>
                <Typography variant="body2">
                  {event.body || i18n.t("campaignsWarmup.empty.noData")}
                </Typography>
              </Box>
            </Box>
            {index < safeEvents.length - 1 ? <Divider /> : null}
          </React.Fragment>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProgressTile = ({ classes, label, value, total, color }) => {
  const safeValue = safeNumber(value);
  const safeTotal = safeNumber(total);
  const percent = safeTotal > 0 ? Math.min((safeValue / safeTotal) * 100, 100) : 0;

  return (
    <Box className={classes.tile}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="caption" className={classes.tileLabel}>{label}</Typography>
        <Typography variant="body2" className={classes.tileValue}>
          {safeValue}/{safeTotal}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        classes={{ colorPrimary: classes.progressTrack }}
        style={{
          height: 10,
          borderRadius: 999,
          background: "rgba(148,163,184,0.16)"
        }}
      />
      <Box
        mt={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="caption" className={classes.tileLabel}>
          {i18n.t("campaignsWarmup.metrics.percentConsumed", { percent: percent.toFixed(0) })}
        </Typography>
        <Box
          style={{
            width: 12,
            height: 12,
            borderRadius: 999,
            background: color
          }}
        />
      </Box>
    </Box>
  );
};

const PeriodDetails = ({ classes, period }) => {
  const data = period || {};
  const hasBaseline = hasPeriodBaseline(data);
  const statItems = [
    { label: i18n.t("campaignsWarmup.metrics.deliveryRate"), value: hasBaseline ? `${safeNumber(data.deliveryRate).toFixed(0)}%` : i18n.t("campaignsWarmup.empty.noData") },
    { label: i18n.t("campaignsWarmup.metrics.readRate"), value: hasBaseline ? `${safeNumber(data.readRate).toFixed(0)}%` : i18n.t("campaignsWarmup.empty.noData") },
    { label: i18n.t("campaignsWarmup.metrics.replyRate"), value: hasBaseline ? `${safeNumber(data.replyRate).toFixed(0)}%` : i18n.t("campaignsWarmup.empty.noData") },
    { label: i18n.t("campaignsWarmup.metrics.positiveReplies"), value: hasBaseline ? safeNumber(data.positiveCount) : i18n.t("campaignsWarmup.empty.noData") },
    { label: i18n.t("campaignsWarmup.metrics.negativeReplies"), value: hasBaseline ? safeNumber(data.negativeCount) : i18n.t("campaignsWarmup.empty.noData") },
    { label: i18n.t("campaignsWarmup.metrics.neutralReplies"), value: hasBaseline ? safeNumber(data.neutralCount) : i18n.t("campaignsWarmup.empty.noData") },
    { label: i18n.t("campaignsWarmup.metrics.optOuts"), value: hasBaseline ? safeNumber(data.optOutCount) : i18n.t("campaignsWarmup.empty.noData") },
    { label: i18n.t("campaignsWarmup.metrics.averageReplyTime"), value: formatSeconds(data.averageReplyTime) }
  ];

  return (
    <Grid container spacing={1.5}>
      {statItems.map(item => (
        <Grid item xs={12} sm={6} md={3} key={item.label}>
          <MetricTile classes={classes} label={item.label} value={item.value} />
        </Grid>
      ))}
    </Grid>
  );
};

const CampaignsWarmup = () => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, socket } = useContext(AuthContext);
  const fallbackTimezone = resolveCompanyTimezone(
    user?.company?.timezone || localStorage.getItem("companyTimezone")
  );

  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [tabValues, setTabValues] = useState({});
  const [eventDialog, setEventDialog] = useState({
    open: false,
    title: "",
    type: "success",
    events: [],
    timezone: fallbackTimezone
  });

  const isAdmin = user.profile === "admin" || user.profile === "superadmin";

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/campaign-warmup/status");
      setConnections(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();

    const channel = `company-${user.companyId}-whatsapp`;
    const handleSocket = data => {
      if (data.action === "update") {
        fetchStatus();
      }
    };

    socket.on(channel, handleSocket);
    return () => socket.off(channel, handleSocket);
  }, [fetchStatus, socket, user.companyId]);

  const handleAction = async (whatsappId, actionName) => {
    try {
      await api.post(`/campaign-warmup/${whatsappId}/${actionName}`);
      toast.success(i18n.t("campaignsWarmup.toasts.actionSuccess"));
      fetchStatus();
    } catch (err) {
      toastError(err);
    }
  };

  const handlePromote = whatsappId => handleAction(whatsappId, "promote");
  const handleRegress = whatsappId => handleAction(whatsappId, "regress");
  const handleUnblock = whatsappId => handleAction(whatsappId, "unblock");
  const handleResetCounters = whatsappId => handleAction(whatsappId, "reset-counters");

  const handleOpenBlockDialog = whatsappId => {
    setSelectedWhatsappId(whatsappId);
    setBlockReason("");
    setBlockDialogOpen(true);
  };

  const handleCloseBlockDialog = () => {
    setBlockDialogOpen(false);
    setSelectedWhatsappId(null);
    setBlockReason("");
  };

  const handleConfirmBlock = async () => {
    if (!blockReason.trim()) {
      toast.error(i18n.t("campaignsWarmup.toasts.blockReasonRequired"));
      return;
    }

    try {
      await api.post(`/campaign-warmup/${selectedWhatsappId}/block`, { reason: blockReason });
      toast.success(i18n.t("campaignsWarmup.toasts.connectionBlocked"));
      handleCloseBlockDialog();
      fetchStatus();
    } catch (err) {
      toastError(err);
    }
  };

  const handleTabChange = (whatsappId, value) => {
    setTabValues(prev => ({ ...prev, [whatsappId]: value }));
  };

  const handleOpenEventDialog = ({ title, type, events, timezone }) => {
    setEventDialog({
      open: true,
      title,
      type,
      events: Array.isArray(events) ? events : [],
      timezone
    });
  };

  const handleCloseEventDialog = () => {
    setEventDialog(prev => ({ ...prev, open: false }));
  };

  const kpis = useMemo(() => {
    const summary = {
      total: connections.length,
      healthy: 0,
      risk: 0,
      blocked: 0,
      sentToday: 0,
      averageSpamRisk: 0,
      baselineConnections: 0
    };

    connections.forEach(conn => {
      const { autoBlocked, manualBlocked } = getBlockedState(conn);
      const health = safeNumber(conn.campaignHealthScore);
      const risk = safeNumber(conn.campaignSpamRiskScore);
      const hasBaseline = hasWarmupBaseline(conn);
      summary.sentToday += safeNumber(conn.campaignSentToday);
      if (hasBaseline) {
        summary.averageSpamRisk += risk;
        summary.baselineConnections += 1;
      }

      if (manualBlocked || autoBlocked || conn.campaignWarmupStage === "STAGE_0_BLOCKED") {
        summary.blocked += 1;
      }
      if (hasBaseline && health >= 80 && risk < 20 && conn.campaignWarmupStage !== "STAGE_0_BLOCKED") {
        summary.healthy += 1;
      }
      if (hasBaseline && (risk >= 20 || health < 80)) {
        summary.risk += 1;
      }
    });

    summary.averageSpamRisk = summary.baselineConnections
      ? Math.round(summary.averageSpamRisk / summary.baselineConnections)
      : null;

    return summary;
  }, [connections]);

  const analyticsData = useMemo(() => {
    const healthBuckets = { saudaveis: 0, atencao: 0, bloqueadas: 0, semBaseline: 0 };
    const stageCount = {};

    connections.forEach(conn => {
      const statusTone = getStatusTone(conn);
      if (statusTone.kind === "healthy") healthBuckets.saudaveis += 1;
      else if (statusTone.kind === "blocked" || statusTone.kind === "manualBlocked") healthBuckets.bloqueadas += 1;
      else if (statusTone.kind === "awaitingBaseline") healthBuckets.semBaseline += 1;
      else healthBuckets.atencao += 1;

      stageCount[conn.campaignWarmupStage] = (stageCount[conn.campaignWarmupStage] || 0) + 1;
    });

    return {
      healthChart: [
        { name: i18n.t("campaignsWarmup.analytics.healthy"), value: healthBuckets.saudaveis },
        { name: i18n.t("campaignsWarmup.status.attention"), value: healthBuckets.atencao },
        { name: i18n.t("campaignsWarmup.analytics.blocked"), value: healthBuckets.bloqueadas },
        { name: i18n.t("campaignsWarmup.empty.noBaseline"), value: healthBuckets.semBaseline }
      ],
      stageDistribution: Object.keys(stageCount).map(stage => ({
        stage: getStageMeta(stage).label,
        total: stageCount[stage]
      })),
      throughput: connections.map(conn => ({
        name: (conn.name || `#${conn.whatsappId}`).slice(0, 14),
        enviados: safeNumber(conn.campaignSentToday),
        limite: safeNumber(conn.campaignDailyLimit)
      }))
    };
  }, [connections]);

  const smartIndicators = useMemo(() => {
    if (!connections.length) return [i18n.t("campaignsWarmup.indicators.noConnection")];

    const items = [];
    const withoutBaseline = connections.filter(conn => !hasWarmupBaseline(conn));
    const risky = connections.filter(conn => hasWarmupBaseline(conn) && (safeNumber(conn.campaignSpamRiskScore) >= 20 || safeNumber(conn.campaignHealthScore) < 60));
    const blocked = connections.filter(conn => {
      const { autoBlocked, manualBlocked } = getBlockedState(conn);
      return autoBlocked || manualBlocked;
    });
    const nearLimit = connections.filter(conn => {
      const dailyLimit = safeNumber(conn.campaignDailyLimit);
      return dailyLimit > 0 && safeNumber(conn.campaignSentToday) / dailyLimit >= 0.8;
    });

    if (withoutBaseline.length) items.push(i18n.t("campaignsWarmup.indicators.withoutBaseline", { count: withoutBaseline.length }));
    if (risky.length) items.push(i18n.t("campaignsWarmup.indicators.risky", { count: risky.length }));
    if (blocked.length) items.push(i18n.t("campaignsWarmup.indicators.blocked", { count: blocked.length }));
    if (nearLimit.length) items.push(i18n.t("campaignsWarmup.indicators.nearLimit", { count: nearLimit.length }));
    if (!items.length) items.push(i18n.t("campaignsWarmup.indicators.stable"));

    return items;
  }, [connections]);

  const renderConnectionCard = conn => {
    const stageMeta = getStageMeta(conn.campaignWarmupStage);
    const statusTone = getStatusTone(conn);
    const activeTab = tabValues[conn.whatsappId] || 0;
    const period = activeTab === 1 ? conn.weeklyReputation : activeTab === 2 ? conn.monthlyReputation : conn.dailyReputation;
    const { autoBlocked, manualBlocked } = getBlockedState(conn);
    const hasBaseline = hasWarmupBaseline(conn);
    const recommendations = Array.isArray(conn.recommendations) ? conn.recommendations : [];
    const history = Array.isArray(conn.recentHistory) ? conn.recentHistory.slice(0, 5) : [];
    const activeCampaigns = Array.isArray(conn.activeCampaigns) ? conn.activeCampaigns : [];
    const connTimezone = conn.timezone || fallbackTimezone;
    const waitingWarmup = conn.waitingWarmup || {
      count: safeNumber(conn.campaignWarmupWaitingCountToday),
      nextRetryAt: null,
      reason: null,
      currentStage: conn.campaignWarmupStage,
      hourlyUsed: conn.campaignSentThisHour,
      hourlyLimit: conn.campaignHourlyLimit,
      dailyUsed: conn.campaignSentToday,
      dailyLimit: conn.campaignDailyLimit,
      autoResume: Array.isArray(conn.warmupEvents) && conn.warmupEvents.some(event => !!event.nextRetryAt)
    };

    return (
      <Card key={conn.whatsappId} className={classes.connectionCard}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.cardHeader}>
            <Box>
              <Box className={classes.identityWrap}>
                <Avatar className={classes.avatar}>
                  {(conn.name || "W").slice(0, 1).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" className={classes.connectionName}>
                    {conn.name || `Conexão #${conn.whatsappId}`}
                  </Typography>
                  <Typography variant="body2" className={classes.metaText}>
                    {i18n.t("campaignsWarmup.labels.number")}: {getConnectionNumber(conn)} · {i18n.t("campaignsWarmup.meta.status")}: {conn.status || i18n.t("campaignsWarmup.empty.noData")} · `whatsappId` {conn.whatsappId}
                  </Typography>
                  <Typography variant="body2" className={classes.metaText}>
                    {i18n.t("campaignsWarmup.meta.lastSend")}: {formatDateTime(conn.campaignLastSentAt, connTimezone)} · {i18n.t("campaignsWarmup.meta.lastStageChange")}: {formatDateTime(conn.campaignLastStageChangeAt, connTimezone)}
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.chipRow}>
                <Chip
                  size="small"
                  label={stageMeta.label}
                  className={classes.stageChip}
                  style={{ color: stageMeta.color, background: stageMeta.bg, border: `1px solid ${stageMeta.color}33` }}
                />
                <Chip
                  size="small"
                  label={statusTone.label}
                  style={{ color: statusTone.color, background: statusTone.bg, border: `1px solid ${statusTone.color}33`, fontWeight: 700 }}
                />
                <Chip size="small" label={manualBlocked ? i18n.t("campaignsWarmup.labels.manualBlock") : autoBlocked ? i18n.t("campaignsWarmup.labels.automaticBlock") : hasBaseline ? i18n.t("campaignsWarmup.status.campaignsReleased") : i18n.t("campaignsWarmup.empty.noOperationalBaseline")} />
              </Box>
            </Box>

            <Box className={classes.headerMetrics}>
              <MiniGauge score={conn.campaignHealthScore} label={i18n.t("campaignsWarmup.metrics.healthScore")} unavailable={!hasBaseline} />
              <Box className={classes.scoreCard}>
                <Typography variant="caption" className={classes.tileLabel}>{i18n.t("campaignsWarmup.metrics.spamRiskScore")}</Typography>
                <Typography
                  variant="h5"
                  className={classes.tileValue}
                  style={{ color: hasBaseline ? getHealthColor(100 - safeNumber(conn.campaignSpamRiskScore)) : "#94a3b8" }}
                >
                  {hasBaseline ? safeNumber(conn.campaignSpamRiskScore) : "—"}
                </Typography>
                <Typography variant="caption" className={classes.tileLabel}>
                  {hasBaseline ? i18n.t("campaignsWarmup.hints.lowerIsBetter") : i18n.t("campaignsWarmup.empty.noCampaignBaseline")}
                </Typography>
              </Box>
            </Box>
          </Box>

          {(manualBlocked || autoBlocked) && (
            <Box className={classes.blockAlert}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                {manualBlocked ? i18n.t("campaignsWarmup.alerts.manualBlockActive") : i18n.t("campaignsWarmup.alerts.automaticBlockActive")}
              </Typography>
              <Typography variant="body2">
                {conn.campaignManualBlockedReason || (autoBlocked ? i18n.t("campaignsWarmup.alerts.blockedUntil", { date: formatDateTime(conn.campaignBlockedUntil, connTimezone) }) : i18n.t("campaignsWarmup.alerts.noBlockDetails"))}
              </Typography>
            </Box>
          )}

          <Box className={classes.sectionBlock}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.sections.connectionCapacity")}</Typography>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} md={6} className={classes.fullWidthGridItem}>
                <ProgressTile
                  classes={classes}
                  label={i18n.t("campaignsWarmup.metrics.dailyLimitVsSent")}
                  value={conn.campaignSentToday}
                  total={conn.campaignDailyLimit}
                  color="#2563eb"
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.fullWidthGridItem}>
                <ProgressTile
                  classes={classes}
                  label={i18n.t("campaignsWarmup.metrics.hourlyLimitVsSent")}
                  value={conn.campaignSentThisHour}
                  total={conn.campaignHourlyLimit}
                  color="#7c3aed"
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.sectionBlock}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.sections.reputationVolume")}</Typography>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.deliveryRate")} value={hasBaseline ? `${safeNumber(conn.deliveryRate).toFixed(0)}%` : i18n.t("campaignsWarmup.empty.noData")} />
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.readRate")} value={hasBaseline ? `${safeNumber(conn.readRate).toFixed(0)}%` : i18n.t("campaignsWarmup.empty.noData")} />
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.replyRate")} value={hasBaseline ? `${safeNumber(conn.replyRate).toFixed(0)}%` : i18n.t("campaignsWarmup.empty.noData")} />
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.deliveredToday")} value={safeNumber(conn.campaignDeliveredCountToday)} />
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.readToday")} value={safeNumber(conn.campaignReadCountToday)} />
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.repliesToday")} value={safeNumber(conn.campaignReplyCountToday)} />
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.sectionBlock}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.sections.dayEvents")}</Typography>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <EventMetricTooltip
                  classes={classes}
                  events={conn.successEvents}
                  type="success"
                  timezone={connTimezone}
                  onClick={() => handleOpenEventDialog({
                    title: "Eventos de sucesso",
                    type: "success",
                    events: conn.successEvents,
                    timezone: connTimezone
                  })}
                >
                  <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.successes")} value={safeNumber(conn.campaignSuccessCountToday)} tone="#10b981" />
                </EventMetricTooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <EventMetricTooltip
                  classes={classes}
                  events={conn.failureEvents}
                  type="failure"
                  timezone={connTimezone}
                  onClick={() => handleOpenEventDialog({
                    title: "Eventos de falha",
                    type: "failure",
                    events: conn.failureEvents,
                    timezone: connTimezone
                  })}
                >
                  <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.failures")} value={safeNumber(conn.campaignFailureCountToday)} tone="#ef4444" />
                </EventMetricTooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <EventMetricTooltip
                  classes={classes}
                  events={conn.optOutEvents}
                  type="optOut"
                  timezone={connTimezone}
                  onClick={() => handleOpenEventDialog({
                    title: "Eventos de opt-out",
                    type: "optOut",
                    events: conn.optOutEvents,
                    timezone: connTimezone
                  })}
                >
                  <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.optOuts")} value={safeNumber(conn.campaignOptOutCountToday)} tone="#f59e0b" />
                </EventMetricTooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <EventMetricTooltip
                  classes={classes}
                  events={conn.warmupEvents}
                  type="warmup"
                  timezone={connTimezone}
                  onClick={() => handleOpenEventDialog({
                    title: "Eventos aguardando warmup",
                    type: "warmup",
                    events: conn.warmupEvents,
                    timezone: connTimezone
                  })}
                >
                  <WaitingWarmupTile classes={classes} summary={waitingWarmup} timezone={connTimezone} />
                </EventMetricTooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.metrics.invalid")} value={safeNumber(conn.campaignInvalidCountToday)} tone="#8b5cf6" />
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.labels.manualBlock")} value={manualBlocked ? i18n.t("common.yes") : i18n.t("common.no")} />
              </Grid>
              <Grid item xs={12} sm={6} md={2} className={classes.fullWidthGridItem}>
                <MetricTile classes={classes} label={i18n.t("campaignsWarmup.labels.automaticBlock")} value={autoBlocked ? i18n.t("common.yes") : i18n.t("common.no")} />
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.sectionBlock}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.sections.periodPerformance")}</Typography>
              <Tabs
                value={activeTab}
                onChange={(event, value) => handleTabChange(conn.whatsappId, value)}
                indicatorColor="primary"
                textColor="primary"
                classes={{ root: classes.periodTabs }}
                variant={isMobile ? "fullWidth" : "standard"}
              >
                <Tab label={i18n.t("campaignsWarmup.tabs.today")} classes={{ root: classes.periodTab }} />
                <Tab label={i18n.t("campaignsWarmup.tabs.sevenDays")} classes={{ root: classes.periodTab }} />
                <Tab label={i18n.t("campaignsWarmup.tabs.thirtyDays")} classes={{ root: classes.periodTab }} />
              </Tabs>
            </Box>
            <Box mt={1.5}>
              <PeriodDetails classes={classes} period={period} />
            </Box>
          </Box>

          <Box className={classes.sectionBlock}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.sections.recommendations")}</Typography>
            <Grid container spacing={1.5} style={{ marginTop: 4 }}>
              {recommendations.length ? (
                recommendations.map(item => (
                  <Grid item xs={12} md={6} key={item} className={classes.fullWidthGridItem}>
                    <Box className={classes.recommendationItem}>
                      <Typography variant="body2">{item}</Typography>
                    </Box>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box className={classes.tile}>
                    <Typography variant="body2">
                      {hasBaseline ? i18n.t("campaignsWarmup.empty.noCriticalRecommendations") : i18n.t("campaignsWarmup.empty.awaitingCampaignData")}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          <Box className={classes.sectionBlock}>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={7} className={classes.fullWidthGridItem}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.sections.recentHistory")}</Typography>
                <Grid container spacing={1.5} style={{ marginTop: 4 }}>
                  {history.length ? history.map(item => (
                      <Grid item xs={12} key={item.id || `${item.action}-${item.createdAt}`} className={classes.fullWidthGridItem}>
                      <Box className={classes.historyItem}>
                        <Typography variant="body2" style={{ fontWeight: 700 }}>
                          {item.action || i18n.t("campaignsWarmup.empty.noAction")} · {item.previousStage || i18n.t("campaignsWarmup.empty.noStage")} → {item.currentStage || i18n.t("campaignsWarmup.empty.noStage")}
                        </Typography>
                        <Typography variant="caption" className={classes.tileLabel}>
                          {i18n.t("campaignsWarmup.metrics.score")} {item.previousScore ?? i18n.t("campaignsWarmup.empty.noData")} → {item.currentScore ?? i18n.t("campaignsWarmup.empty.noData")} · {formatDateTime(item.createdAt, conn.timezone || fallbackTimezone)}
                        </Typography>
                      </Box>
                    </Grid>
                  )) : (
                    <Grid item xs={12}>
                      <Box className={classes.tile}>
                        <Typography variant="body2">{i18n.t("campaignsWarmup.empty.noRecentHistory")}</Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} lg={5} className={classes.fullWidthGridItem}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.sections.activeCampaigns")}</Typography>
                <Box mt={1}>
                  {activeCampaigns.length ? activeCampaigns.map(campaign => (
                    <Chip
                      key={campaign.id}
                      label={`${campaign.name || i18n.t("campaignsWarmup.fallbacks.campaign", { id: campaign.id })} · ${campaign.status || i18n.t("campaignsWarmup.empty.noData")}`}
                      className={classes.campaignChip}
                    />
                  )) : (
                    <Box className={classes.tile}>
                      <Typography variant="body2">{i18n.t("campaignsWarmup.empty.noActiveCampaigns")}</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider style={{ margin: "20px 0 16px" }} />

          <Box className={classes.actionBar}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<Autorenew />}
              onClick={fetchStatus}
            >
              {i18n.t("common.buttons.refresh")}
            </Button>
            {isAdmin && (
              <>
                <Button size="small" variant="outlined" onClick={() => handlePromote(conn.whatsappId)} startIcon={<ArrowUpward />}>
                  {i18n.t("campaignsWarmup.actions.promote")}
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleRegress(conn.whatsappId)} startIcon={<ArrowDownward />}>
                  {i18n.t("campaignsWarmup.actions.regress")}
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleOpenBlockDialog(conn.whatsappId)} startIcon={<Block />}>
                  {i18n.t("campaignsWarmup.actions.block")}
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleUnblock(conn.whatsappId)} startIcon={<CheckCircleOutline />}>
                  {i18n.t("campaignsWarmup.actions.unblock")}
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleResetCounters(conn.whatsappId)} startIcon={<RotateLeft />}>
                  {i18n.t("campaignsWarmup.actions.resetCounters")}
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <MainContainer style={{ padding: 0 }} className={classes.pageRoot}>
      <div className={classes.dashboardPageWrapper}>
        <Paper className={classes.mainPaper} variant="outlined">
      <Box className={classes.hero}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" className={classes.heroTitle}>
              {i18n.t("campaignsWarmup.title")}
            </Typography>
            <Typography variant="body1" className={classes.heroSubtitle}>
              {i18n.t("campaignsWarmup.subtitle")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"} flexWrap="wrap" style={{ gap: 12 }}>
              <Box className={classes.heroPill}>
                <Typography variant="caption">{i18n.t("campaignsWarmup.labels.connections")}</Typography>
                <Typography variant="h6" style={{ fontWeight: 800 }}>{kpis.total}</Typography>
              </Box>
              <Box className={classes.heroPill}>
                <Typography variant="caption">{i18n.t("campaignsWarmup.labels.sentToday")}</Typography>
                <Typography variant="h6" style={{ fontWeight: 800 }}>{kpis.sentToday}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2}>
        {[
          { label: i18n.t("campaignsWarmup.kpis.totalConnections"), value: kpis.total, hint: i18n.t("campaignsWarmup.kpis.monitoredNow"), color: "#2563eb" },
          { label: i18n.t("campaignsWarmup.kpis.healthy"), value: kpis.healthy, hint: i18n.t("campaignsWarmup.kpis.highScoreLowRisk"), color: "#10b981" },
          { label: i18n.t("campaignsWarmup.kpis.atRisk"), value: kpis.risk, hint: i18n.t("campaignsWarmup.kpis.needReview"), color: "#f59e0b" },
          { label: i18n.t("campaignsWarmup.kpis.blocked"), value: kpis.blocked, hint: i18n.t("campaignsWarmup.kpis.manualOrAutomatic"), color: "#ef4444" },
          { label: i18n.t("campaignsWarmup.labels.sentToday"), value: kpis.sentToday, hint: i18n.t("campaignsWarmup.kpis.consumedCapacity"), color: "#7c3aed" },
          { label: i18n.t("campaignsWarmup.kpis.averageSpamRisk"), value: kpis.averageSpamRisk === null ? i18n.t("campaignsWarmup.empty.noData") : `${kpis.averageSpamRisk}/100`, hint: kpis.averageSpamRisk === null ? i18n.t("campaignsWarmup.status.awaitingBaseline") : i18n.t("campaignsWarmup.hints.lowerIsBetter"), color: "#06b6d4" }
        ].map(card => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={card.label}>
            <Card className={`${classes.sectionCard} ${classes.kpiCard}`}>
              <CardContent>
                <Typography variant="body2" className={classes.kpiHint}>{card.label}</Typography>
                <Typography variant="h4" className={classes.kpiValue} style={{ color: card.color }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" className={classes.kpiHint}>{card.hint}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card className={`${classes.sectionCard} ${classes.analyticsCard}`}>
        <Box className={classes.analyticsHeader}>
          <Box>
            <Typography variant="h6" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.sections.analytics")}</Typography>
            <Typography variant="body2" className={classes.metaText}>
              {i18n.t("campaignsWarmup.analytics.summary")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={showAnalytics ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowAnalytics(prev => !prev)}
          >
            {showAnalytics ? i18n.t("campaignsWarmup.analytics.hide") : i18n.t("campaignsWarmup.analytics.show")}
          </Button>
        </Box>

        <Collapse in={showAnalytics}>
          <Box className={classes.analyticsBody}>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={5}>
                <Box className={classes.chartShell}>
                  <Typography variant="subtitle1" className={classes.sectionTitle} style={{ marginBottom: 12 }}>
                    {i18n.t("campaignsWarmup.analytics.dailyConsumption")}
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.throughput}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <ChartTooltip />
                      <Legend />
                      <Bar dataKey="enviados" fill="#2563eb" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="limite" fill="#10b981" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box className={classes.chartShell}>
                  <Typography variant="subtitle1" className={classes.sectionTitle} style={{ marginBottom: 12 }}>
                    {i18n.t("campaignsWarmup.analytics.operationalHealth")}
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={analyticsData.healthChart} dataKey="value" nameKey="name" innerRadius={50} outerRadius={78} paddingAngle={3}>
                        {analyticsData.healthChart.map((item, index) => (
                          <Cell key={item.name} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend />
                      <ChartTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Box className={classes.chartShell}>
                  <Typography variant="subtitle1" className={classes.sectionTitle} style={{ marginBottom: 12 }}>
                    {i18n.t("campaignsWarmup.analytics.stageDistribution")}
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.stageDistribution} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="stage" tick={{ fontSize: 11 }} width={132} />
                      <ChartTooltip />
                      <Bar dataKey="total" fill="#7c3aed" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box className={classes.smartCard}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    {i18n.t("campaignsWarmup.analytics.quickIndicators")}
                  </Typography>
                  <Grid container spacing={2} style={{ marginTop: 4 }}>
                    {smartIndicators.map(indicator => (
                      <Grid item xs={12} md={6} key={indicator}>
                        <Box className={classes.smartItem}>
                          <Warning color="primary" fontSize="small" />
                          <Typography variant="body2">{indicator}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Card>

      {loading ? (
        <Box py={8} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : !connections.length ? (
        <Card className={`${classes.sectionCard} ${classes.emptyState}`}>
          <Smartphone style={{ fontSize: 38, marginBottom: 12, opacity: 0.65 }} />
          <Typography variant="h6" className={classes.sectionTitle}>{i18n.t("campaignsWarmup.empty.noConnectionFound")}</Typography>
          <Typography variant="body2">
            {i18n.t("campaignsWarmup.empty.noConnectionDescription")}
          </Typography>
        </Card>
      ) : (
        <Box className={classes.cardsList}>
          {connections.map(renderConnectionCard)}
        </Box>
      )}

      <Dialog open={blockDialogOpen} onClose={handleCloseBlockDialog} fullWidth maxWidth="sm">
        <DialogTitle>{i18n.t("campaignsWarmup.dialog.blockConnectionTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("campaignsWarmup.dialog.blockConnectionDescription")}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={i18n.t("campaignsWarmup.labels.blockReason")}
            type="text"
            fullWidth
            multiline
            minRows={3}
            value={blockReason}
            onChange={e => setBlockReason(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBlockDialog}>{i18n.t("common.buttons.cancel")}</Button>
          <Button onClick={handleConfirmBlock} color="secondary" variant="contained">
            {i18n.t("campaignsWarmup.actions.confirmBlock")}
          </Button>
        </DialogActions>
      </Dialog>

      <EventDetailsDialog
        classes={classes}
        open={eventDialog.open}
        onClose={handleCloseEventDialog}
        title={eventDialog.title}
        type={eventDialog.type}
        events={eventDialog.events}
        timezone={eventDialog.timezone}
      />
        </Paper>
      </div>
    </MainContainer>
  );
};

export default CampaignsWarmup;
